import { BTPaper } from "@common-components/atoms/bt-paper/bt-paper";
import FormsIncludedColumnsDialog from '@common-components/tasks-panel/form/forms-included-columns-dialog';
import KanbanColumnPicker from '@common-components/tasks-panel/kanban/kanban-column-picker';
import BoardHideColumnsDialog from "@common-components/tasks-panel/tasks-panel-header/board-included-columns-dialog";
import WorkloadIncludedColumnsDialog from '@common-components/tasks-panel/workload/workload-included-columns-dialog';
import { Popper } from '@mui/base/Popper';
import { ClickAwayListener } from "@mui/material";
import { pickerDialogActions } from "@common-reducers/PickerDialogReducer";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { useShadowDOM } from "@/common/contexts/ShadowDOMContext";
import { usePortalContainer } from "@/common/hooks/use-portal-container";

import { createElement, JSXElementConstructor, memo, ReactElement, useMemo, useState } from "react";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import BoardMenuOptions from "../board-menu-options/board-menu-options";
import BoardMenuPicker from "../board-menu-picker/board-menu-picker";
import { PickerProps } from "../columns-cells/cell-interface";
import ComplexityPicker from "../columns-cells/complexity-picker/complexity-picker";
import OptionPicker from "../columns-cells/option-picker/option-picker";
// import TimeTrackerDialog from "../columns-cells/time-tracker-cell/time-tracker-dialog";
import BoardViewIncludedColumnsDialog from "@common-components/tasks-panel/board-views/board-view-included-columns-dialog";
import PersonPicker from "../person-picker/person-picker";
import Addlink from "./Addlink";
import Datepicker from "./Datepicker";
import AddColumnDialog from "./pickers/add-column-dialog";
import ColumnContextMenuDialog from "./pickers/column-context-menu-dialog";
import Dropdown from "./pickers/Dropdown";
import FilesMenu from "./pickers/filesMenu";
import TimelinePicker from "./TimelinePicker";

const PickerTypeList = [
  'add-column-picker',
  'option-picker',
  'complexity-picker',
  'column-context-menu',
  'date-picker',
  'timeline-picker',
  'drop-down',
  'person-picker',
  'board-menu-picker',
  'link-menu',
  'kanban-drawer',
  'gantt-drawer',
  'forms-included-columns-dialog',
  'board-view-included-columns-dialog',
  'workload-included-columns-dialog',
  'time-tracker',
  'board-menu-options'
]
export type PickerType = typeof PickerTypeList[number];

const pickerTypeMap: Record<PickerType, JSXElementConstructor<PickerProps>> = {
  'add-column-picker': AddColumnDialog,
  'option-picker': OptionPicker,
  'complexity-picker': ComplexityPicker,
  'column-context-menu': ColumnContextMenuDialog,
  'date-picker': Datepicker,
  'timeline-picker': TimelinePicker || Datepicker,
  'files': FilesMenu,
  'drop-down': Dropdown,
  'person-picker': PersonPicker,
  'board-menu-picker': BoardMenuPicker,
  'link-menu': Addlink,
  'hide-columns-dialog': BoardHideColumnsDialog,
  'kanban-column-picker': KanbanColumnPicker,
  'forms-included-columns-dialog': FormsIncludedColumnsDialog,
  'board-view-included-columns-dialog': BoardViewIncludedColumnsDialog,
  'workload-included-columns-dialog': WorkloadIncludedColumnsDialog,
  'board-menu-options':BoardMenuOptions,

  // 'time-tracker': TimeTrackerDialog,
}

function buildAnchorSelector(id?: string): string | null {
  if (!id) {
    return null;
  }
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return `#${CSS.escape(id)}`;
  }
  if (/^[A-Za-z0-9\-_]+$/.test(id)) {
    return `#${id}`;
  }
  return null;
}

export type OnPickerCloseCallback = () => void;

export interface Props {
  boardId: string;
}

function PickerDialog({ boardId }: Props) {
  const dispatch = useAppDispatch()
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
  const pickerProps = useAppSelector((state) => state.PickerDialogReducer.pickerProps);
  const pickerAnchorId = useAppSelector((state) => state.PickerDialogReducer.pickerAnchorId);
  const { shadowRoot } = useShadowDOM();
  const portalContainer = usePortalContainer();
  const mediaManager = useMemo(() => {
    if (!pickerAnchorId) {
      return null;
    }
    const mediaFromShadow = shadowRoot?.getElementById("media-manager") as HTMLElement | null | undefined;
    if (mediaFromShadow) {
      return mediaFromShadow;
    }
    if (shadowRoot) {
      const selector = buildAnchorSelector("media-manager");
      if (selector) {
        const queryResult = shadowRoot.querySelector(selector) as HTMLElement | null;
        if (queryResult) {
          return queryResult;
        }
      }
    }
    return document.getElementById("media-manager");
  }, [pickerAnchorId, shadowRoot]);

  const popperAnchorEl = useMemo(() => {
    if (!pickerAnchorId) {
      return null;
    }

    const selector = buildAnchorSelector(pickerAnchorId);

    if (mediaManager && selector) {
      const anchorInsideMedia = mediaManager.querySelector(selector) as HTMLElement | null;
      if (anchorInsideMedia) {
        return anchorInsideMedia;
      }
    }

    if (shadowRoot) {
      const anchorById = shadowRoot.getElementById(pickerAnchorId) as HTMLElement | null;
      if (anchorById) {
        return anchorById;
      }
      if (selector) {
        const anchorBySelector = shadowRoot.querySelector(selector) as HTMLElement | null;
        if (anchorBySelector) {
          return anchorBySelector;
        }
      }
    }

    if (portalContainer instanceof HTMLElement && selector) {
      const portalMatch = portalContainer.querySelector(selector) as HTMLElement | null;
      if (portalMatch) {
        return portalMatch;
      }
    } else if (portalContainer instanceof ShadowRoot) {
      const portalMatch = portalContainer.getElementById(pickerAnchorId) as HTMLElement | null;
      if (portalMatch) {
        return portalMatch;
      }
      if (selector) {
        const portalSelectorMatch = portalContainer.querySelector(selector) as HTMLElement | null;
        if (portalSelectorMatch) {
          return portalSelectorMatch;
        }
      }
    }

    return document.getElementById(pickerAnchorId);
  }, [pickerAnchorId, mediaManager, portalContainer, shadowRoot]);

  let pickerContentComponent: ReactElement;


  if (!pickerAnchorId) return <></>;
  let popperWidth: any = 150;



  function toggleMenu() {
    dispatch(pickerDialogActions.closePickerDialog());
  }

  function onClickAway(_event: MouseEvent | TouchEvent) {
    toggleMenu();
  }

  const renderPicker = () => {
    if (pickerProps?.type) {
      pickerContentComponent = createElement(pickerTypeMap[pickerProps.type], pickerProps)
    }

    return pickerContentComponent;
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div id="generalPicker">

        <Popper
          open={!!pickerAnchorId}
          style={{ zIndex: 9999, minWidth: popperWidth }}
          anchorEl={popperAnchorEl}
          placement={pickerProps?.placement ?? "bottom"}
          container={portalContainer instanceof ShadowRoot ? (portalContainer.host as HTMLElement) : portalContainer as HTMLElement | undefined}
          disablePortal={false}
          modifiers={[
            {
              name: "arrow",
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
            {
              name: "flip",
              enabled: true,
              options: {
                boundary: "viewport",
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
              },
            },
          ]}
        >
          <>
            <span ref={setArrowRef} />
            <BTPaper style={{
              padding: '0px !important',
              borderRadius: '4px !important',
              background: 'var(--color-surface) !important',
              boxShadow: '0 8px 16px 0px var(--tasks-shadow) !important'
            }}>
              <div>
                {renderPicker()}
              </div >
            </BTPaper >
          </>
        </Popper >

      </div >
    </ClickAwayListener >

  );
}

export default memo(PickerDialog);
